import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Upload, Trash2, Play, Pause, Music, X } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterNotificationSoundsContent = () => {
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [name, setName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchSounds = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/master/notification-sounds`);
      setSounds(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar sons:', err);
      toast.error('Erro ao carregar sons de notificação');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSounds();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePreview = (sound) => {
    try {
      if (playingId === sound.id && audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setPlayingId(null);
        return;
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(sound.url);
      audioRef.current.onended = () => setPlayingId(null);
      audioRef.current.play().catch(() => toast.error('Não foi possível reproduzir o áudio'));
      setPlayingId(sound.id);
    } catch (e) {
      toast.error('Erro ao reproduzir');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Áudio muito grande. Máximo 2MB.');
      return;
    }
    setSelectedFile(file);
    if (!name) {
      setName(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleUpload = async () => {
    if (!name.trim()) {
      toast.error('Informe um nome para o som');
      return;
    }
    if (!selectedFile) {
      toast.error('Selecione um arquivo de áudio');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('file', selectedFile);
      await axios.post(`${API}/master/notification-sounds`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Som adicionado com sucesso!');
      setShowAddModal(false);
      setName('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchSounds();
    } catch (err) {
      console.error('Erro ao enviar som:', err);
      toast.error(err.response?.data?.detail || 'Erro ao enviar som');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (sound) => {
    if (!window.confirm(`Remover o som "${sound.name}"?`)) return;
    try {
      await axios.delete(`${API}/master/notification-sounds/${sound.id}`);
      toast.success('Som removido');
      fetchSounds();
    } catch (err) {
      toast.error('Erro ao remover som');
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-lg">
            <Bell className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">Sons de Notificação</h1>
            <p className="text-sm text-slate-400">Biblioteca de sons padrão disponíveis para todas as lojas</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold shadow-lg hover:opacity-90 transition"
          data-testid="add-sound-btn"
        >
          <Upload size={18} /> Adicionar Som
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 py-12 text-center">Carregando sons...</div>
      ) : sounds.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-slate-700 bg-slate-800/30">
          <Music className="mx-auto text-slate-500 mb-3" size={40} />
          <p className="text-slate-300 font-medium">Nenhum som padrão cadastrado</p>
          <p className="text-slate-500 text-sm mt-1">Clique em Adicionar Som para enviar o primeiro.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sounds.map((sound) => (
            <div
              key={sound.id}
              className="rounded-2xl border border-slate-700/60 bg-slate-800/50 p-4 flex items-center justify-between gap-3"
              data-testid={`sound-card-${sound.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => handlePreview(sound)}
                  className="w-11 h-11 flex-shrink-0 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center text-white shadow hover:opacity-90 transition"
                  title="Ouvir"
                >
                  {playingId === sound.id ? <Pause size={18} /> : <Play size={18} />}
                </button>
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{sound.name}</p>
                  <p className="text-slate-500 text-xs truncate">{sound.filename}</p>
                </div>
              </div>
              <button
                onClick={() => handleDelete(sound)}
                className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition flex-shrink-0"
                title="Remover"
                data-testid={`delete-sound-${sound.id}`}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">Adicionar Som Padrão</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <label className="block text-sm font-medium text-slate-300 mb-2">Nome do som</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Campainha, Sino..."
              className="w-full mb-4 px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              data-testid="sound-name-input"
            />

            <label className="block text-sm font-medium text-slate-300 mb-2">Arquivo de áudio (máx. 2MB)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              className="w-full mb-2 text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-500 file:text-white file:font-medium hover:file:opacity-90"
              data-testid="sound-file-input"
            />
            {selectedFile && (
              <p className="text-xs text-slate-400 mb-4">Selecionado: {selectedFile.name}</p>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 rounded-xl bg-slate-700 text-white font-medium hover:bg-slate-600 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
                data-testid="confirm-upload-sound-btn"
              >
                {uploading ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterNotificationSoundsContent;
